import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useConfirm } from 'material-ui-confirm';

import {
  createProviderPayingAccountsAction,
  disconnectProviderPayingAccountsAction,
  updateProviderPayingAccountsAction,
} from 'domain/paySettings';

import useSearchAndSort from 'pages/configurations/hooks/useSearchAndSort';
import { useAccountListColumns } from './useAccountListColumns';
import usePaymentProvidersConfig from 'pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/hooks/usePaymentProvidersConfig';
import useGetAccounts from './useGetAccountsData';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import SearchBaseRaw from 'components/mui/Layouts/components/AppbarSearchBase';
const SearchBase = SearchBaseRaw as any;
import TableRaw from 'pages/configurations/components/Table';
const Table = TableRaw as any;
import CircularProgressWithBackdrop from 'components/mui/CircularProgressWithBackdrop';
import DialogAddGlobalAccount from '../DialogAddGlobalAccount';
import DialogAccountDetails from '../DialogAccountDetails';

import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

const defaultSortFn = (accounts: unknown[]) => accounts;

interface AccountListProps {
  providerId: string;
  providerName: string;
}

const AccountList: React.FC<AccountListProps> = ({ providerId, providerName }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const paymentProviderConfig = usePaymentProvidersConfig(providerName as any);

  const [updatingAccount, setUpdatingAccount] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);

  const { data, isLoading, setData, bankAccounts } = useGetAccounts(providerId);

  const handleCloseAddModal = useCallback(() => {
    setOpenAddModal(false);
    setSelectedAccount(null);
  }, []);

  const createAccount = useCallback(
    (accountData: any) => {
      setUpdatingAccount(true);
      new Promise((resolve, reject) => {
        dispatch(
          createProviderPayingAccountsAction({
            payload: { ...accountData, id: providerId },
            resolve,
            reject,
          }),
        );
      })
        .then((accounts) => {
          setData(accounts);
          handleCloseAddModal();
        })
        .finally(() => setUpdatingAccount(false));
    },
    [dispatch, providerId, setData, setUpdatingAccount, handleCloseAddModal],
  );

  const updateAccount = useCallback(
    ({ id, ...rest }: { id: any; [key: string]: any }) => {
      setUpdatingAccount(true);
      new Promise((resolve, reject) => {
        dispatch(
          updateProviderPayingAccountsAction({
            payload: { payingAccountId: id, id: providerId, ...rest },
            resolve,
            reject,
          }),
        );
      })
        .then((accounts) => {
          setData(accounts);
          handleCloseAddModal();
        })
        .finally(() => setUpdatingAccount(false));
    },
    [dispatch, providerId, setData, setUpdatingAccount, handleCloseAddModal],
  );

  const handleSuspend = useCallback(
    ({ id, isSuspended }: { id: any; isSuspended: boolean }) => {
      if (!isSuspended) {
        updateAccount({ id, isSuspended });
      } else {
        confirm({
          title: formatMessage({
            id: 'configurations.company.paymentProviders.detailsModal.accountList.confirmSuspend.title',
            defaultMessage: 'Suspend payment account?',
          }),
          description: formatMessage({
            id: 'configurations.company.paymentProviders.detailsModal.accountList.confirmSuspend.description',
            defaultMessage:
              'Are you sure you want to suspend this payment account? Transactions will be temporarily blocked until reactivated. You can resume it anytime.',
          }),
          confirmationText: formatMessage({
            id: 'configurations.company.paymentProviders.detailsModal.accountList.confirmSuspend.confirmBtn',
            defaultMessage: 'Suspend account',
          }),
          confirmationButtonProps: { color: 'error' },
        })
          .then(() => {
            updateAccount({ id, isSuspended });
          })
          .catch(() => {});
      }
    },
    [formatMessage, updateAccount, confirm],
  );

  const disconnectAccount = useCallback(
    (id: any) => {
      setUpdatingAccount(true);
      new Promise((resolve, reject) => {
        dispatch(
          disconnectProviderPayingAccountsAction({
            payload: { payingAccountId: id, id: providerId },
            resolve,
            reject,
          }),
        );
      })
        .then((accounts) => {
          setData(accounts);
        })
        .finally(() => setUpdatingAccount(false));
    },
    [dispatch, providerId, setData],
  );

  const handleDisconnect = useCallback(
    (id: any) => {
      confirm({
        title: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.confirmDisconnect.title',
          defaultMessage: 'Disconnect payment account?',
        }),
        description: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.confirmDisconnect.description',
          defaultMessage:
            'Are you sure you want to disconnect this payment account?\nThis action will remove the account from your system, and you will need to reconnect it to use it again.',
        }),
        confirmationText: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.confirmDisconnect.confirmBtn',
          defaultMessage: 'Disconnect account',
        }),
        confirmationButtonProps: { color: 'error' },
      })
        .then(() => {
          disconnectAccount(id);
        })
        .catch(() => {});
    },
    [formatMessage, disconnectAccount, confirm],
  );

  const handleCloseDetailsModal = useCallback(() => {
    setOpenDetailsModal(false);
    setSelectedAccount(null);
  }, []);

  const handleOpenDetailsModal = useCallback((account: any) => {
    setOpenDetailsModal(true);
    setSelectedAccount(account);
  }, []);

  const handleOpenEditModal = useCallback((account: any) => {
    setOpenAddModal(true);
    setSelectedAccount(account);
  }, []);

  const columns = useAccountListColumns({
    handleSuspend,
    handleDisconnect,
    providerName,
    handleOpenDetailsModal,
    handleOpenEditModal,
  });

  const handleAddButton = useCallback(() => {
    setOpenAddModal(true);
  }, []);

  const shouldDisableAddButton = useMemo(() => {
    return paymentProviderConfig?.walletsLimit
      ? (data as any[]).filter(({ status }: any) => status !== 'CLOSED').length >= paymentProviderConfig.walletsLimit
      : false;
  }, [data, paymentProviderConfig?.walletsLimit]);

  const generateStringForSearch = useCallback(
    (account: any) => {
      return columns
        .filter((col) => col.searchable)
        .map((col) => account[col.id])
        .filter(Boolean) // reject undefined/null
        .join(' ');
    },
    [columns],
  );

  const { debounceOnChangeSearch, handleChangeSort, searchText, rows, sort, sortBy } = useSearchAndSort({
    generateStringForSearch,
    initialList: data,
    defaultSortFn,
  });

  return (
    <>
      <CircularProgressWithBackdrop isOpen={updatingAccount && !openAddModal} />
      <Stack p={2} flexDirection="row" justifyContent="space-between">
        <SearchBase value={searchText} onChange={debounceOnChangeSearch} />
        <Button
          type="button"
          startIcon={<AddOutlinedIcon />}
          onClick={handleAddButton}
          disabled={shouldDisableAddButton}
        >
          {formatMessage({
            id: 'configurations.company.paymentProviders.detailsModal.accountList.addAccountBtn',
            defaultMessage: 'Add global account',
          })}
        </Button>
      </Stack>
      {isLoading ? (
        <LinearProgress />
      ) : (
        <Table data={rows} sort={sort} sortBy={sortBy} columns={columns} handleChangeSort={handleChangeSort} />
      )}
      {openAddModal && (
        <DialogAddGlobalAccount
          onClose={handleCloseAddModal}
          handleCreate={createAccount}
          handleUpdate={updateAccount}
          selectedAccount={selectedAccount}
          bankAccounts={bankAccounts}
          providerName={providerName as any}
          providerId={providerId}
          updatingAccount={updatingAccount}
        />
      )}
      {openDetailsModal && selectedAccount && (
        <DialogAccountDetails onClose={handleCloseDetailsModal} data={selectedAccount} />
      )}
    </>
  );
};

export default AccountList;
